import {
    CARD_TYPES,
    EGIFT_DELIVERY_EMAIL,
    EGIFT_DELIVERY_SHAREABLE
} from '../constants/constants';

const filterAndMapKeys = (originData, keyMap) => {
    const result = {};
    Object.keys(keyMap).forEach((key) => {
        if (originData && Object.prototype.hasOwnProperty.call(originData, key)) {
            result[keyMap[key]] = originData[key];
        }
    });
    return result;
};

const cartGiftCardKeyMap = {
    faceValue: 'cardValue',
    faceplateCode: 'faceplateCode',
    brandCode: 'brandName',
    cartItemToken: 'id',
    recipientEmail: 'recipientEmail',
    recipientName: 'recipientName',
    senderName: 'yourName',
    senderEmail: 'yourEmail',
    selfBuy: 'selfBuy',
    deliveryDate: 'deliveryDate',
    message: 'personalMessage',
    isSharedViaLink: 'isSharedViaLink',
    quantity: 'quantity',
    multipleRecipients: 'multipleRecipients',
    uyoImageUrl: 'UYOPImageURL'
};

export const cartItemDeserializer = (item, getFaceplateByCodeFn) => {
    const mappedItem = filterAndMapKeys(item, cartGiftCardKeyMap);

    const cardValue = parseFloat(mappedItem.cardValue);
    if (Number.isNaN(cardValue)) {
        mappedItem.cardValue = 0;
    } else {
        mappedItem.cardValue = cardValue;
    }

    const shareOption = mappedItem.isSharedViaLink
        ? EGIFT_DELIVERY_SHAREABLE : EGIFT_DELIVERY_EMAIL;
    mappedItem.eGiftCardShareOption = shareOption;

    
    if (mappedItem.isSharedViaLink && !mappedItem.recipientEmail) {
        mappedItem.recipientEmail = item.senderEmail || '';
        mappedItem.recipientName = item.senderName || '';
    }

    // For self-buy items, set yourEmail to recipientEmail if available
    if (mappedItem.selfBuy && mappedItem.recipientEmail) {
        mappedItem.yourEmail = mappedItem.recipientEmail;
    }

    if (item.senderName) {
        mappedItem.senderName = item.senderName;
    }
    if (item.senderEmail) {
        mappedItem.senderEmail = item.senderEmail;
    }

    const storedFaceplate = getFaceplateByCodeFn
        ? getFaceplateByCodeFn(mappedItem.faceplateCode) : null;

    // Fallback to uyoImageUrl from item if faceplate not found in Redux
    const fallbackImageUrl = item.uyoImageUrl || '';

    const { faceplateCode, brandName } = mappedItem;
    const faceplate = storedFaceplate ? {
        code: storedFaceplate.get('code'),
        imageURL: storedFaceplate.get('imageURL') || fallbackImageUrl,
        altText: storedFaceplate.get('altText', `${brandName} Gift Card`),
        captionText: storedFaceplate.get('captionText', `${brandName} Gift Card`),
        textColor: storedFaceplate.get('textColor', 'BLACK'),
        category: storedFaceplate.get('category', 'Gift Cards'),
        ordinal: storedFaceplate.get('ordinal', 1),
        denomination: storedFaceplate.get('denomination', null)
    } : {
        code: faceplateCode,
        imageURL: fallbackImageUrl,
        altText: `${brandName} Gift Card`,
        captionText: `${brandName} Gift Card`,
        textColor: 'BLACK',
        category: 'Gift Cards',
        ordinal: 1,
        denomination: null
    };

    mappedItem.faceplate = faceplate;
    delete mappedItem.faceplateCode;
    delete mappedItem.brandName;

    
    mappedItem.associatedRecipientIds = [];
    mappedItem.quantity = mappedItem.quantity || 1;

    return mappedItem;
};

export const cartResponseDeserializer = (capiResponse, getFaceplateByCodeFn) => {
    const checkoutData = capiResponse?.checkout_request_data;

    if (!checkoutData) {
        return [];
    }

    const allItems = [];
    const { uyoImageURL: topLevelImageUrl = '' } = checkoutData;

    const { egiftCards } = checkoutData;
    if (egiftCards && egiftCards.length > 0) {
        const digitalItems = egiftCards.map((item) => {
            const itemWithImageUrl = {
                ...item,
                uyoImageUrl: item.uyoImageUrl || topLevelImageUrl
            };
            const deserializedItem = cartItemDeserializer(
                itemWithImageUrl,
                getFaceplateByCodeFn
            );

            deserializedItem.itemCardType = CARD_TYPES.Digital;
            return deserializedItem;
        });
        allItems.push(...digitalItems);
    }

    // Process physical cards from shippingGroups
    const { shippingGroups } = checkoutData;
    if (shippingGroups && shippingGroups.length > 0) {
        shippingGroups.forEach((group) => {
            if (group.certs && group.certs.length > 0) {
                const physicalItems = group.certs.map((item) => {
                    const deserializedItem = cartItemDeserializer(
                        item,
                        getFaceplateByCodeFn
                    );

                    deserializedItem.itemCardType = CARD_TYPES.Physical;

                    deserializedItem.shippingAddress = {
                        street1: group.shippingAddress.street,
                        street2: group.shippingAddress.street2,
                        city: group.shippingAddress.city,
                        state: group.shippingAddress.state,
                        postalCode: group.shippingAddress.zipCode,
                        countryCode: group.shippingAddress.countryCode,
                        fullName: group.shippingAddress.fullName,
                        token: group.shippingMethodToken
                    };

                    deserializedItem.shippingAmount = group.shippingAmount;
                    const shippingDisplayName = capiResponse.shipping_group
                        || 'Standard Shipping';
                    deserializedItem.displayData = {
                        shippingDisplayName: {
                            [`${group.shippingMethodToken}_displayText`]:
                                shippingDisplayName,
                            [`${group.shippingMethodToken}_displayCost`]:
                                group.shippingAmount
                        }
                    };
                    return deserializedItem;
                });
                allItems.push(...physicalItems);
            }
        });
    }

    return allItems;
};
